import { mayaApiClient } from '~/utils/mayaApiClient';
import { maxMessageId, mergeAnswerResponse } from '~/utils/mayaSessionMerge';

export { maxMessageId, mergeAnswerResponse } from '~/utils/mayaSessionMerge';

export const state = () => ({
  session: null,
});

export const mutations = {
  SET_SESSION(state, session) {
    state.session = session;
  },
  MERGE_SESSION(state, patch) {
    if (!state.session) {
      return;
    }

    state.session = { ...state.session, ...patch };
  },
  MERGE_ANSWER_RESPONSE(state, answerPayload) {
    state.session = mergeAnswerResponse(state.session, answerPayload);
  },
};

function mayaPath(isPublic, taskSlug, suffix) {
  const prefix = isPublic ? '/public/participant' : '/participant';

  return `${prefix}/task/${taskSlug}/maya/${suffix}`;
}

function participantParams(state, query = {}, isPublic = true) {
  const params = { ...query };

  if (isPublic && state.session?.uuid) {
    params.uuid = state.session.uuid;
  }

  return params;
}

export const actions = {
  async start({ commit, state }, { taskSlug, query = {}, isPublic = true, email = null }) {
    const payload = participantParams(state, query, isPublic);

    if (email) {
      payload.email = email;
    }

    const { data } = await mayaApiClient().post(
      mayaPath(isPublic, taskSlug, 'start'),
      payload
    );
    commit('SET_SESSION', data);
    return data;
  },

  async refresh({ commit, state }, { taskSlug, query = {}, isPublic = true, full = false }) {
    const params = participantParams(state, query, isPublic);

    if (! full) {
      const afterMessageId = maxMessageId(state.session?.messages);

      if (afterMessageId > 0) {
        params.after_message_id = String(afterMessageId);
      }
    }

    const { data } = await mayaApiClient().get(
      mayaPath(isPublic, taskSlug, 'state'),
      { params }
    );

    if (params.after_message_id) {
      commit('MERGE_ANSWER_RESPONSE', data);
    } else {
      commit('SET_SESSION', data);
    }

    return data;
  },

  async submitAnswer({ commit, state }, {
    taskSlug,
    payload,
    query = {},
    isPublic = true,
    signal = null,
    onUploadProgress = null,
  }) {
    const formData = new FormData();
    if (payload.text) {
      formData.append('text', payload.text);
    }
    if (payload.media) {
      const files = Array.isArray(payload.media) ? payload.media : [payload.media];

      files.forEach((file) => {
        formData.append('media[]', file);
      });
    }

    const afterMessageId = maxMessageId(state.session?.messages);
    if (afterMessageId > 0) {
      formData.append('after_message_id', String(afterMessageId));
    }

    Object.entries(participantParams(state, query, isPublic)).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });

    const requestConfig = {
      timeout: 120000,
      transformRequest: [(payload, headers) => {
        if (payload instanceof FormData) {
          delete headers['Content-Type'];
        }

        return payload;
      }],
    };

    if (signal) {
      requestConfig.signal = signal;
    }

    if (typeof onUploadProgress === 'function') {
      requestConfig.onUploadProgress = onUploadProgress;
    }

    const { data } = await mayaApiClient().post(
      mayaPath(isPublic, taskSlug, 'answer'),
      formData,
      requestConfig
    );
    commit('MERGE_ANSWER_RESPONSE', data);
    return data;
  },

  async advance({ commit, state }, { taskSlug, query = {}, isPublic = true }) {
    const payload = participantParams(state, query, isPublic);
    const afterMessageId = maxMessageId(state.session?.messages);

    if (afterMessageId > 0) {
      payload.after_message_id = String(afterMessageId);
    }

    const { data } = await mayaApiClient().post(
      mayaPath(isPublic, taskSlug, 'advance'),
      payload,
      { timeout: 120000 }
    );

    if (afterMessageId > 0) {
      commit('MERGE_ANSWER_RESPONSE', data);
    } else {
      commit('SET_SESSION', data);
    }

    return data;
  },

  async contact({ commit, state }, { taskSlug, email, query = {}, isPublic = true }) {
    const { data } = await mayaApiClient().post(
      mayaPath(isPublic, taskSlug, 'contact'),
      {
        email,
        ...participantParams(state, query, isPublic),
      }
    );

    commit('MERGE_SESSION', data);

    return data;
  },

  async fetchDictationToken({ state }, {
    taskSlug,
    locale,
    query = {},
    isPublic = true,
  }) {
    const { data } = await mayaApiClient().post(
      mayaPath(isPublic, taskSlug, 'dictate/token'),
      {
        ...participantParams(state, query, isPublic),
        locale,
      }
    );

    return data;
  },
};
